/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Group, Skeleton, Text, rem, useMantineTheme } from '@mantine/core';
import { isNil } from 'es-toolkit';

import { useFinetuningStatusWithPhase } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/use-finetuning-status-with-phase';
import Tag from '@fai/ui/components/tag';
import { TextVariants } from '@fai/ui/components/typography/variants';

import {
  useFinetuningInitializingPhaseVariants,
  useFinetuningStatusVariants,
} from './finetuning-tag-variants';

export default function FinetuningTag({
  finetuningId,
  showPhase,
}: {
  finetuningId?: string;
  showPhase?: boolean;
}) {
  const theme = useMantineTheme();
  const statusVariants = useFinetuningStatusVariants();
  const initializingPhaseVariants = useFinetuningInitializingPhaseVariants();

  const { status, phase } = useFinetuningStatusWithPhase(finetuningId);

  if (isNil(status)) return <Skeleton w={rem(120)} h={rem(24)} />;

  return (
    <Group gap={theme.pf.layout['3xs']} wrap="nowrap">
      <Tag
        bg={statusVariants[status]?.backgroundColor}
        c={statusVariants[status]?.color}
        icon={statusVariants[status]?.icon}
        bd={`1px solid ${statusVariants[status]?.borderColor}`}
        style={{ borderRadius: theme.pf.radius.sm }}
      >
        {`${statusVariants[status]?.content}`}
      </Tag>

      {showPhase && phase?.initializingStep && (
        <Group
          c={initializingPhaseVariants[phase.initializingStep].color}
          gap={theme.pf.layout['4xs']}
          wrap="nowrap"
          style={{ flexShrink: 1 }}
        >
          {initializingPhaseVariants[phase.initializingStep].icon}

          <Text
            flex={1}
            variant={TextVariants.TitleXs}
            lineClamp={1}
            style={{ wordBreak: 'break-all' }}
          >
            {initializingPhaseVariants[phase.initializingStep].content}
          </Text>
        </Group>
      )}
    </Group>
  );
}

FinetuningTag.displayName = 'FinetuningTag';
